import {useState, useEffect, useCallback, useMemo} from 'react'
import addBusinessDays from 'date-fns/addBusinessDays'
import parseISO from 'date-fns/parseISO'
import isSameDay from 'date-fns/isSameDay'

import {getState, useSelector} from '../../../store.js'
import DatePicker from '../../../common/components/DatePicker.js'
import {
  updateLabelConfig,
  labelConfigSelector,
  isLabelPropertyEnabledSelector,
} from '../../../data/labelInfos/index.js'
import EnableLabelProperty, {PickLabelProperty} from './EnableLabelProperty.js'
import useLabelProperty, {formatMetaClassName} from './useLabelProperty.js'
import className from '../../../common/className.js'
import {
  AMAZON_SFP,
  AUSTRALIA_POST,
  DHL,
  DHL_ECOMMERCE,
  ENDICIA,
  FEDEX,
  FEDEX_360,
  PITNEY,
  PITNEY_MERCHANT,
  SENDLE,
  SHIPPO,
  USPS_SHIP,
} from '../../../common/constants/ShipperNames.js'
import {useLabelConfigContext} from '../LabelConfigContext.js'

export const SHIP_DATE_VALID_SHIPPER_TYPES = [
  AMAZON_SFP,
  AUSTRALIA_POST,
  DHL_ECOMMERCE,
  DHL,
  ENDICIA,
  FEDEX,
  FEDEX_360,
  PITNEY,
  PITNEY_MERCHANT,
  SHIPPO,
  USPS_SHIP,
]

export const PICKUP_DATE_VALID_SHIPPER_TYPES = [DHL, SENDLE]

/**
 * Reusable Date input for future dates for label config
 *
 * @param {Object} props
 * @param {String} props.label
 * @param {Boolean} [props.nextBusinessDay]
 * @param {Object} [props.propertySpecs]
 * @param {Function} props.propertySpecs.labelPropertiesFunc
 * @param {Array} props.propertySpecs.validShipperTypes
 * @param {Function} props.propertySpecs.dependentPropertiesFunc
 * @param {Function} props.propertySpecs.validLabelPropertiesFunc
 * @returns {React.ReactNode}
 */
export default function ForwardDate({label, nextBusinessDay, propertySpecs}) {
  const {
    labelInfoID,
    shipperType,
    onChange,
    isLabelEnableMode,
    isSummary,
    isPickMode,
  } = useLabelConfigContext()
  const [labelProperty] = propertySpecs.labelPropertiesFunc(
    shipperType,
    labelInfoID,
  )

  const id = `forward_date__${labelInfoID}__${labelProperty}`

  const dateStr = useSelector(
    (state) => labelConfigSelector(state, {labelInfoID})[labelProperty],
  )
  const [minDate, setMinDate] = useState(
    nextBusinessDay ? addBusinessDays(new Date(), 1) : new Date(),
  )
  const date = useMemo(() => (dateStr ? parseISO(dateStr) : null), [dateStr])

  useLabelProperty({
    labelProperty,
    propertySpecs,
  })

  const isLabelPropertyEnabled = useSelector((state) =>
    isLabelPropertyEnabledSelector(state, {
      labelInfoID,
      labelProperty,
    }),
  )

  const hide = !propertySpecs.validShipperTypes.includes(shipperType)

  const disabled = isLabelEnableMode && !isLabelPropertyEnabled

  const onDateChange = useCallback(
    (date) => {
      if (hide) {
        return
      }

      const origDateStr = labelConfigSelector(getState(), {labelInfoID})[
        labelProperty
      ]
      const newDateStr = date ? date.toISOString() : null

      if (
        origDateStr &&
        newDateStr &&
        isSameDay(parseISO(origDateStr), parseISO(newDateStr))
      ) {
        return
      }

      updateLabelConfig(labelInfoID, {
        [labelProperty]: newDateStr,
      })

      if (onChange) {
        onChange()
      }
    },
    [labelProperty, onChange, hide],
  )

  const setupDates = useCallback(() => {
    if (hide) {
      return
    }

    const minDate = nextBusinessDay
      ? addBusinessDays(new Date(), 1)
      : new Date()

    setMinDate(minDate)

    if (date && date < minDate) {
      onDateChange(minDate)
    }
  }, [onChange, hide])

  useEffect(() => {
    if (hide) {
      return
    }

    setupDates()

    const intervalID = setInterval(setupDates, 1000 * 60 * 10)

    return () => clearInterval(intervalID)
  }, [setupDates, hide])

  if (hide) {
    return null
  }

  if (isSummary) {
    return label
  }

  if (isPickMode) {
    return <PickLabelProperty label={label} labelProperty={labelProperty} />
  }

  return (
    <li
      className={className`list__item--shipping-options ${formatMetaClassName(
        labelProperty,
      )} ${{
        'property-disabled': disabled,
      }}`}
    >
      <div className="wrap--edit-preset-form-input wrap--edit-preset-form-input-md">
        <label
          className="label--bold label--forward-date label--config__txt"
          htmlFor={id}
        >
          {label}
        </label>
        <DatePicker
          id={id}
          minDate={minDate}
          onChange={onDateChange}
          selected={date}
          disabled={disabled}
        />
      </div>
      <EnableLabelProperty labelProperty={labelProperty} />
    </li>
  )
}
